import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { FOCUSABLE } from './Drawer'

/**
 * A centred dialog drawn as a card over the page — the operator tier's answer to
 * "open this row" and "add one of these".
 *
 * 🔴 WHY THIS EXISTS RATHER THAN A SEVENTH HAND-ROLLED OVERLAY. Six screens in
 * this app already write their own `fixed inset-0 … flex items-center` block,
 * and no two of them agree on how many of the five behaviours below they
 * implement. Every one that is missing is invisible in a screenshot and a bug
 * for somebody: the operator who cannot find the exit, the reader whose Tab key
 * walks the table behind the dialog, the phone user whose page scrolls under it.
 *
 * ⚠️ IT IS MOUNT-DRIVEN, NOT `open`-DRIVEN — unlike `Drawer`, which animates
 * and so must outlive its own dismissal. Render it inside the condition
 * (`{selected !== null && <ModalCard …>}`) and the effects below start and unwind
 * with the dialog itself, which is the only way the focus restore can know what
 * had focus before it opened.
 *
 * ── What this owns ────────────────────────────────────────────────────────
 *
 *   · Escape closes it       — on `document`, not the panel, because the control
 *                              that opened the dialog is behind the overlay and
 *                              may still be where focus was when it mounted.
 *   · Pressing outside closes — see the note on `onMouseDown`; it is the press
 *                              that decides, not the click.
 *   · Focus moves in, and back
 *   · Focus is trapped
 *   · The page behind cannot scroll
 *
 * The CARD's own chrome is the caller's: pass a `Panel`, a form, whatever the
 * screen already draws. This owns the overlay and the five behaviours, and
 * nothing about how the contents look.
 */
export interface ModalCardProps {
  /**
   * What a screen reader is told this dialog is. Required — an unlabelled
   * dialog is announced as "dialog" and nothing else.
   */
  label: string
  onClose: () => void
  /** Card width. The operator tier's forms are two columns wide at `2xl`. */
  width?: string
  children: ReactNode
}

export function ModalCard({ label, onClose, width = 'max-w-2xl', children }: ModalCardProps) {
  const card = useRef<HTMLDivElement>(null)
  /*
   * ⚠️ CAPTURED ON MOUNT, NOT ON CLOSE. By the time this unmounts, focus is
   * inside a card that is being removed from the document — at which point the
   * browser drops focus to `<body>` and the reader's place in a table of every
   * school is gone.
   */
  const returnTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    returnTo.current = document.activeElement as HTMLElement | null

    /*
     * 🔴 THE CARD ITSELF, NOT ITS FIRST FIELD. Focusing the first input
     * announces "Code, edit text" and never says a dialog opened at all; the
     * card carries `role="dialog"` and the label, so focusing it announces the
     * dialog and the first Tab then reaches the first control. `Drawer` carries
     * the same argument for the same reason.
     */
    card.current?.focus()
    document.documentElement.classList.add('scroll-locked')

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.documentElement.classList.remove('scroll-locked')
      /* Guarded: the row that opened this can be re-rendered away by the very
         refetch the dialog's own save triggered. */
      const back = returnTo.current
      if (back?.isConnected) back.focus()
    }
  }, [onClose])

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const items = card.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
    if (!items || items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    /* The trap: wrap at both ends rather than letting focus reach the page. */
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }, [])

  const pressedOutside = useRef(false)

  return (
    /*
     * ⚠️ THE OVERLAY SCROLLS AND DOES NOT ALIGN; THE WRAPPER INSIDE IT ALIGNS
     * AND DOES NOT SCROLL. A container that does both puts the top of a
     * too-tall dialog above its own scroll origin, where nothing can reach it —
     * and these dialogs are forms of six fields and a checkbox.
     */
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-ink/40 backdrop-blur-xs"
      /*
       * 🔴 WHERE THE PRESS STARTED, NOT WHERE THE CLICK LANDED. React fires
       * `click` on the common ancestor of the press and the release, so somebody
       * who selects the text in a field and lets go a few pixels past its edge
       * releases over the backdrop — and a plain `onClick` here would read that
       * as "dismiss" and throw the half-typed form away.
       */
      onMouseDown={(e) => {
        pressedOutside.current = !card.current?.contains(e.target as Node)
      }}
      onClick={() => {
        if (pressedOutside.current) onClose()
      }}
      onKeyDown={onKeyDown}
    >
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={card}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          tabIndex={-1}
          className={`w-full ${width} my-8 outline-none`}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export default ModalCard
